import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { IInitiateDepositRequest } from "@finbackoffice/clientbff-client";
import { useForm, useWatch } from "react-hook-form";
import { MultiWalletDataContext, WalletsContext } from "contexts";
import { ClientBFFContext } from "@finbackoffice/site-core";
import { IDepositForm } from "components/base/wallet/multi-wallet-pro/pages/common/hooks/bank/useCoreDepositFormBank";

export const useDepositFormMulti = () => {
    const client = useContext(ClientBFFContext);
    const { currentCurrency } = useContext(WalletsContext);
    const { setDepositResponse, setStep, selectedPaymentMethod, selectedProvider } =
        useContext(MultiWalletDataContext);
    const [cardNumber, setCardNumber] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const formMethods = useForm<IDepositForm>({
        mode: "onChange",
        defaultValues: { amount: "" },
    });
    const formValues = useWatch({ control: formMethods.control });

    useEffect(() => {
        setError("");
    }, [formValues]);

    const clickDeposit = useCallback(
        async (data: IDepositForm) => {
            if (!selectedProvider || !currentCurrency || !selectedPaymentMethod) return;
            try {
                setLoading(true);
                const payload: IInitiateDepositRequest = {
                    provider: selectedProvider,
                    currency: currentCurrency,
                    amount: data.amount,
                    provider_data: {
                        payment_method: selectedPaymentMethod,
                    },
                };
                const response = await client.initiateDeposit(payload);
                if (response.deposit) {
                    setDepositResponse(response.deposit);
                    setStep("iframe");
                }
            } catch (err: any) {
                const error = err.response.data ?? {};

                if (error.error) {
                    const details = error.details;
                    switch (error.error) {
                        case "payments_fulfillment_exception": {
                            let detailedMessage = "";

                            if (details) {
                                if (details.max_deposit_error) {
                                    detailedMessage = details.max_deposit_error;
                                } else {
                                    for (const key in details) {
                                        detailedMessage += details[key] + "\n";
                                    }
                                }
                            }

                            if (detailedMessage) {
                                setError(detailedMessage);
                            } else {
                                setError(error.message);
                            }
                            break;
                        }
                        case "validation_error": {
                            if (details) {
                                if (details.provider_data) {
                                    for (const field in details.provider_data) {
                                        let detailedMessage = "";
                                        for (const key in details.provider_data[field]) {
                                            detailedMessage +=
                                                details.provider_data[field][key] + "\n";
                                        }
                                        formMethods.setError(field as keyof IDepositForm, {
                                            message: detailedMessage,
                                        });
                                    }
                                }
                            } else {
                                setError(error.message);
                            }
                            break;
                        }
                        default:
                            setError(error.message);
                    }
                }
            } finally {
                setLoading(false);
            }
        },
        [
            selectedProvider,
            currentCurrency,
            selectedPaymentMethod,
            client,
            setDepositResponse,
            setStep,
            formMethods,
        ],
    );

    return useMemo(
        () => ({
            clickDeposit,
            error,
            loading,
            cardNumber,
            setCardNumber,
            formMethods,
        }),
        [cardNumber, clickDeposit, error, formMethods, loading],
    );
};
